// FAT-9: painel de estatísticas do backtest. Estados: vazio (sem resultado), insuficiente (aviso + números
// brutos, sem leitura de Sharpe/Sortino/PSR/IBOV) e completo. Resultado passado não garante resultado futuro.
import React from 'react';
import './EstatisticasHonestas.css';
import { formatPercentOuIndisponivel, formatProbabilidade, formatRazao } from '../utils/backtestRisk';

const AVISO = 'Resultado passado não garante resultado futuro. O FatCat não executa ordens.';

function Celula({ rotulo, valor, explicacao }) {
  return (
    <div className="eh-cell" title={explicacao}>
      <span className="eh-label">{rotulo}</span>
      <strong className="eh-value">{valor}</strong>
      <small className="eh-help">{explicacao}</small>
    </div>
  );
}

export default function EstatisticasHonestas({ backtest, testId = 'estatisticas-honestas' }) {
  const e = backtest?.estatisticasHonestas;
  if (!e) {
    return (
      <section className="eh-panel eh-empty" data-testid={`${testId}-vazio`}>
        <h4>Estatísticas do backtest</h4>
        <p>Sem resultado: execute o job de backtests para calcular Sharpe, Sortino, PSR e a comparação com o IBOV.</p>
      </section>
    );
  }
  const comuns = [
    ['Trades', e.tradesTotal == null ? 'indisponível' : String(e.tradesTotal), 'Quantidade de operações simuladas no período.'],
    ['Custos totais', formatPercentOuIndisponivel(e.custosTotaisPct), 'Soma dos custos da B3 e do slippage simulados nos trades, em pontos percentuais.'],
    ['Exposição média', e.exposicaoMediaPct == null ? 'indisponível' : `${Number(e.exposicaoMediaPct).toFixed(1).replace('.', ',')}%`, 'Percentual dos pregões do período com ao menos uma posição aberta.'],
    ['Retorno líquido', formatPercentOuIndisponivel(e.retornoLiquidoPct), 'Retorno composto da curva já descontados custos e slippage.'],
  ];
  if (e.amostraInsuficiente) {
    return (
      <section className="eh-panel eh-insufficient" data-testid={`${testId}-insuficiente`}>
        <h4>Estatísticas do backtest</h4>
        <p role="note" className="eh-warning">
          Amostra insuficiente: {e.tradesTotal ?? 0} trades, mínimo de {e.minimoTrades ?? '—'}. Sharpe, Sortino, PSR e
          comparação com o IBOV não são exibidos porque, com poucos trades, não permitem conclusão.
        </p>
        <div className="eh-grid">
          {comuns.map(([r, v, h]) => <Celula key={r} rotulo={r} valor={v} explicacao={h} />)}
        </div>
        <p className="eh-disclaimer">{AVISO}</p>
      </section>
    );
  }
  const sortino = e.sortinoInfinito ? '∞ (sem pregões negativos)' : formatRazao(e.sortino);
  return (
    <section className="eh-panel" data-testid={testId}>
      <h4>Estatísticas do backtest</h4>
      <div className="eh-grid">
        <Celula rotulo="Sharpe anualizado" valor={formatRazao(e.sharpe)} explicacao="Retorno diário líquido médio dividido pela oscilação, anualizado (252 pregões, sem taxa livre de risco). Mede retorno por unidade de oscilação." />
        <Celula rotulo="Sortino anualizado" valor={sortino} explicacao="Como o Sharpe, mas considera só a oscilação das perdas diárias." />
        <Celula rotulo="Sharpe probabilístico (PSR)" valor={formatProbabilidade(e.psr)} explicacao="Probabilidade de o Sharpe verdadeiro ser maior que zero, dado o tamanho e o formato da amostra. Não é probabilidade de ganho futuro." />
        <Celula rotulo="Retorno excedente vs IBOV" valor={formatPercentOuIndisponivel(e.retornoExcedenteIbovPct)} explicacao="Retorno líquido da estratégia menos o do IBOV nos mesmos pregões." />
        <Celula rotulo="Beta vs IBOV" valor={formatRazao(e.betaIbov)} explicacao="Sensibilidade da estratégia às variações diárias do IBOV; 1 acompanha o índice, 0 não tem relação." />
        {comuns.map(([r, v, h]) => <Celula key={r} rotulo={r} valor={v} explicacao={h} />)}
      </div>
      <p className="eh-disclaimer">{AVISO}</p>
    </section>
  );
}
